// src/pages/BusinessDashboard.jsx
import React, { useEffect, useMemo, useState } from "react";
import supabase from "../supabaseClient";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";

const bucket = { logo: "avatars", banner: "banners" };
const defaultCenter = { lat: -33.8688, lng: 151.2093 };

async function ensureBusinessProfile() {
  const { data: { user }, error: uerr } = await supabase.auth.getUser();
  if (uerr || !user) throw uerr || new Error("No user");
  const { data, error } = await supabase
    .from("business_profiles")
    .select("*")
    .eq("user_id", user.id)
    .maybeSingle();

  if (error && error.code !== "PGRST116") throw error;
  if (!data) {
    const { data: created, error: cerr } = await supabase
      .from("business_profiles")
      .insert({
        user_id: user.id,
        business_name: "",
        industry: "",
        location: "",
        about: "",
      })
      .select("*")
      .single();
    if (cerr) throw cerr;
    return created;
  }
  return data;
}

function filePath(userId, filename) {
  const safe = filename.replace(/\s+/g, "_");
  return `${userId}/${crypto.randomUUID()}_${safe}`;
}

const Uploader = ({ accept, onPick }) => (
  <input type="file" accept={accept} onChange={(e) => e.target.files?.[0] && onPick(e.target.files[0])} />
);

export default function BusinessDashboard() {
  const [loading, setLoading] = useState(true);
  const [biz, setBiz] = useState(null);
  const [saving, setSaving] = useState(false);

  const [incoming, setIncoming] = useState([]);
  const [connections, setConnections] = useState([]);

  const [skillsQuery, setSkillsQuery] = useState("");
  const [publicOnly, setPublicOnly] = useState(true);
  const [talentResults, setTalentResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const { isLoaded: mapsReady } = useJsApiLoader({
    id: "gmap-script",
    googleMapsApiKey: import.meta.env.VITE_GMAPS_API_KEY,
  });

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const p = await ensureBusinessProfile();
        setBiz(p);
        await refreshConnections(p.id);
      } catch (e) {
        console.error("Init failed", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function refreshConnections(businessId) {
    const { data: pend } = await supabase
      .from("connections")
      .select(`
        id, status, created_at,
        talent_profile_id, business_id,
        talent_profiles!inner(id, full_name, headline, avatar_url)
      `)
      .eq("business_id", businessId)
      .eq("status", "pending")
      .order("created_at", { ascending: false });

    setIncoming(pend || []);

    const { data: acc } = await supabase
      .from("connections")
      .select(`
        id, status, created_at,
        talent_profile_id, business_id,
        talent_profiles!inner(id, full_name, headline, avatar_url)
      `)
      .eq("business_id", businessId)
      .eq("status", "accepted")
      .order("created_at", { ascending: false });

    setConnections(acc || []);
  }

  async function uploadTo(bucketName, file) {
    const { data: { user } } = await supabase.auth.getUser();
    const path = filePath(user.id, file.name);
    const { error: upErr } = await supabase.storage.from(bucketName).upload(path, file, { upsert: false });
    if (upErr) throw upErr;
    return path;
  }

  function publicUrl(bucketName, path) {
    if (!path) return "";
    const { data } = supabase.storage.from(bucketName).getPublicUrl(path);
    return data.publicUrl;
  }

  async function saveBiz(patch) {
    if (!biz) return;
    setSaving(true);
    const { data, error } = await supabase
      .from("business_profiles")
      .update({ ...biz, ...patch })
      .eq("id", biz.id)
      .select("*")
      .single();
    setSaving(false);
    if (error) return console.error("Save failed", error);
    setBiz(data);
  }

  async function setLogo(file) {
    try {
      const path = await uploadTo(bucket.logo, file);
      await saveBiz({ logo_url: path });
    } catch (e) { console.error(e); }
  }

  async function setBanner(file) {
    try {
      const path = await uploadTo(bucket.banner, file);
      await saveBiz({ banner_url: path });
    } catch (e) { console.error(e); }
  }

  async function acceptConn(id) {
    const { error } = await supabase.from("connections").update({ status: "accepted" }).eq("id", id);
    if (error) return console.error(error);
    await refreshConnections(biz.id);
  }

  async function rejectConn(id) {
    const { error } = await supabase.from("connections").update({ status: "rejected" }).eq("id", id);
    if (error) return console.error(error);
    await refreshConnections(biz.id);
  }

  async function searchTalents() {
    setSearching(true);
    try {
      let query = supabase
        .from("talent_profiles")
        .select("id, full_name, headline, avatar_url, skills, public_view_enabled")
        .order("updated_at", { ascending: false })
        .limit(100);

      if (publicOnly) query = query.eq("public_view_enabled", true);

      const skills = skillsQuery.split(",").map((s) => s.trim()).filter(Boolean);
      const { data, error } = await query;
      if (error) throw error;

      let list = data || [];
      if (skills.length > 0) {
        const lowered = skills.map((s) => s.toLowerCase());
        list = list.filter((t) => {
          const arr = Array.isArray(t.skills) ? t.skills : [];
          const low = arr.map((x) => String(x).toLowerCase());
          return lowered.every((s) => low.includes(s));
        });
      }
      setTalentResults(list);
    } catch (e) {
      console.error(e);
    } finally {
      setSearching(false);
    }
  }

  if (loading) return <div className="p-6">Loading…</div>;

  return (
    <div className="p-6 grid grid-cols-12 gap-6">
      {/* LEFT */}
      <div className="col-span-12 lg:col-span-7 space-y-6">
        <div className="p-4 rounded-2xl shadow border">
          <h2 className="text-xl font-semibold mb-3">Business Profile</h2>

          <div className="flex items-start gap-6">
            <div>
              <div className="text-sm mb-1">Logo</div>
              <Uploader accept="image/*" onPick={setLogo} />
              {biz?.logo_url && (
                <img
                  src={publicUrl(bucket.logo, biz.logo_url)}
                  alt="logo"
                  className="mt-2 w-24 h-24 object-cover rounded-xl border"
                />
              )}
            </div>
            <div className="flex-1">
              <div className="text-sm mb-1">Banner</div>
              <Uploader accept="image/*" onPick={setBanner} />
              {biz?.banner_url && (
                <img
                  src={publicUrl(bucket.banner, biz.banner_url)}
                  alt="banner"
                  className="mt-2 w-full h-28 object-cover rounded-xl"
                />
              )}
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <input
              className="border rounded-lg p-2"
              placeholder="Business name"
              value={biz?.business_name || ""}
              onChange={(e) => setBiz((b) => ({ ...b, business_name: e.target.value }))}
            />
            <input
              className="border rounded-lg p-2"
              placeholder="Industry (Finance, SaaS, Recruitment…)"
              value={biz?.industry || ""}
              onChange={(e) => setBiz((b) => ({ ...b, industry: e.target.value }))}
            />
            <input
              className="border rounded-lg p-2"
              placeholder="Location (City/State)"
              value={biz?.location || ""}
              onChange={(e) => setBiz((b) => ({ ...b, location: e.target.value }))}
            />
            <input
              className="border rounded-lg p-2"
              placeholder="Website"
              value={biz?.website || ""}
              onChange={(e) => setBiz((b) => ({ ...b, website: e.target.value }))}
            />
            <textarea
              className="col-span-2 border rounded-lg p-2"
              rows={3}
              placeholder="About the business"
              value={biz?.about || ""}
              onChange={(e) => setBiz((b) => ({ ...b, about: e.target.value }))}
            />
          </div>

          {/* Map (optional: if you later add lat/lng columns) */}
          <div className="mt-4">
            <div className="text-sm mb-2">Approximate Location (optional)</div>
            <div className="h-64 rounded-xl overflow-hidden border">
              {mapsReady ? (
                <GoogleMap
                  mapContainerStyle={{ width: "100%", height: "100%" }}
                  center={defaultCenter}
                  zoom={10}
                >
                  {biz?.lat && biz?.lng && <Marker position={{ lat: biz.lat, lng: biz.lng }} />}
                </GoogleMap>
              ) : (
                <div className="p-4 text-sm">Loading map…</div>
              )}
            </div>
          </div>

          <div className="mt-4 flex items-center gap-3">
            <button
              disabled={saving}
              onClick={() => saveBiz({})}
              className="px-3 py-2 rounded-xl bg-black text-white text-sm disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>

        {/* Talent Finder */}
        <div className="p-4 rounded-2xl shadow border">
          <h2 className="text-xl font-semibold mb-3">Talent Finder</h2>
          <div className="grid grid-cols-4 gap-3 items-end">
            <div className="col-span-3">
              <label className="text-xs block mb-1 opacity-70">Skills (comma separated)</label>
              <input
                className="w-full border rounded-lg p-2"
                placeholder="e.g. React, Node, Leadership"
                value={skillsQuery}
                onChange={(e) => setSkillsQuery(e.target.value)}
              />
            </div>
            <div className="col-span-1">
              <label className="text-xs block mb-1 opacity-70">Visibility</label>
              <label className="text-sm flex items-center gap-2 border rounded-lg p-2">
                <input type="checkbox" checked={publicOnly} onChange={() => setPublicOnly((v) => !v)} />
                public only
              </label>
            </div>
            <div className="col-span-4">
              <button onClick={searchTalents} className="px-3 py-2 rounded-xl border shadow text-sm" disabled={searching}>
                {searching ? "Searching…" : "Search"}
              </button>
            </div>
          </div>

          <ul className="mt-4 divide-y">
            {talentResults.map((t) => (
              <li key={t.id} className="py-3 flex items-center gap-3">
                <img
                  src={t.avatar_url ? supabase.storage.from("avatars").getPublicUrl(t.avatar_url).data.publicUrl : ""}
                  alt=""
                  className="w-10 h-10 rounded-full object-cover border"
                  onError={(e) => (e.currentTarget.style.display = "none")}
                />
                <div className="flex-1">
                  <div className="font-medium">{t.full_name || "Unnamed"}</div>
                  <div className="text-xs opacity-70">{t.headline}</div>
                  <div className="text-xs opacity-60 mt-1">
                    {Array.isArray(t.skills) ? t.skills.slice(0, 8).join(" • ") : ""}
                  </div>
                </div>
                <span className="text-[11px] px-2 py-1 rounded border">
                  {t.public_view_enabled ? "public" : "restricted"}
                </span>
                <button
                  className="text-sm underline ml-2"
                  onClick={async () => {
                    const { error } = await supabase.from("connections").insert({
                      talent_profile_id: t.id,
                      business_id: biz.id,
                    });
                    if (error) return console.error(error);
                    alert("Connection requested.");
                  }}
                >
                  Request connect
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* RIGHT */}
      <div className="col-span-12 lg:col-span-5 space-y-6">
        <div className="p-4 rounded-2xl shadow border">
          <h3 className="font-semibold mb-3">Incoming Requests</h3>
          {incoming.length === 0 ? (
            <div className="text-sm opacity-60">No pending requests.</div>
          ) : (
            <ul className="space-y-2">
              {incoming.map((r) => (
                <li key={r.id} className="border rounded-xl p-3 flex items-center gap-3">
                  <img
                    src={
                      r.talent_profiles?.avatar_url
                        ? supabase.storage.from("avatars").getPublicUrl(r.talent_profiles.avatar_url).data.publicUrl
                        : ""
                    }
                    alt=""
                    className="w-8 h-8 rounded-full object-cover border"
                    onError={(e) => (e.currentTarget.style.display = "none")}
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium">{r.talent_profiles?.full_name || "Talent"}</div>
                    <div className="text-xs opacity-60">{r.talent_profiles?.headline}</div>
                  </div>
                  <button className="text-xs px-3 py-1 rounded-xl border" onClick={() => acceptConn(r.id)}>
                    Accept
                  </button>
                  <button className="text-xs px-3 py-1 rounded-xl border" onClick={() => rejectConn(r.id)}>
                    Reject
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="p-4 rounded-2xl shadow border">
          <h3 className="font-semibold mb-3">My Connections</h3>
          {connections.length === 0 ? (
            <div className="text-sm opacity-60">No accepted connections yet.</div>
          ) : (
            <ul className="space-y-2">
              {connections.map((c) => (
                <li key={c.id} className="border rounded-xl p-3 flex items-center gap-3">
                  <img
                    src={
                      c.talent_profiles?.avatar_url
                        ? supabase.storage.from("avatars").getPublicUrl(c.talent_profiles.avatar_url).data.publicUrl
                        : ""
                    }
                    alt=""
                    className="w-8 h-8 rounded-full object-cover border"
                    onError={(e) => (e.currentTarget.style.display = "none")}
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium">{c.talent_profiles?.full_name || "Talent"}</div>
                    <div className="text-xs opacity-60">{c.talent_profiles?.headline}</div>
                  </div>
                  <button
                    className="text-xs underline"
                    onClick={() => {
                      // Route to chat
                      window.location.href = `/chat/${c.id}`;
                    }}
                  >
                    Open Chat
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
